import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { ExternalLink, FileText } from "lucide-react";
import { AppPageShell } from "@/components/ui/AppPageShell/AppPageShell";
import { Button } from "@/components/ui/button";
import {
  fileContentPagesQueryOptions,
  fileContentQueryOptions,
} from "@/lib/data/react-query/file-content";
import { getFileContentSource } from "@/lib/data/requests/file-content";
import { automationError } from "@/components/features/automations/automationError";
export function FileContentPage({ id }: { id: string }) {
  const file = useQuery(fileContentQueryOptions(id));
  const pages = useInfiniteQuery(
    fileContentPagesQueryOptions(id, file.data?.processingId ?? ""),
  );
  const [links, setLinks] = useState<Record<string, string>>({});
  const source = useMutation({
    mutationFn: ({
      page,
      processingId,
    }: {
      page: number;
      processingId: string;
    }) => getFileContentSource(id, processingId, page),
    onSuccess: (result, { processingId }) => {
      setLinks((current) => ({
        ...current,
        ...Object.fromEntries(
          result.data.map((page) => [
            `${processingId}:${page.pageNumber}`,
            page.url,
          ]),
        ),
      }));
    },
  });
  return (
    <AppPageShell contentClassName="overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl space-y-6 pb-10">
        {file.isPending && <p role="status">Loading file…</p>}
        {file.error && <p role="alert">{automationError(file.error)}</p>}
        {file.data && (
          <>
            <div>
              <p className="text-muted-foreground mb-2 text-sm">
                {file.data.sourceKind === "firm" ? "Firm file" : "Case file"}
              </p>
              <h1 className="flex items-center gap-3 text-2xl font-semibold">
                <FileText className="size-6 shrink-0" />
                {file.data.fileName}
              </h1>
            </div>
            <div className="flex flex-wrap gap-2">
              {file.data.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="flex items-center gap-2 rounded-full border px-3 py-1 text-sm"
                >
                  <span
                    className="size-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </span>
              ))}
            </div>
            {file.data.documentDescription && (
              <p className="leading-7 whitespace-pre-wrap">
                {file.data.documentDescription}
              </p>
            )}
            {!file.data.processingId && (
              <p
                role="status"
                className="text-muted-foreground rounded-lg border p-5"
              >
                {file.data.status === "failed"
                  ? "This file could not be processed."
                  : "This file is processing. Its description and page summaries will appear when ready."}
              </p>
            )}
            {!!file.data.processingId && (
              <div className="border-t pt-5">
                <h2 className="mb-4 font-semibold">
                  Pages{file.data.pageCount ? ` · ${file.data.pageCount}` : ""}
                </h2>
                {pages.isPending && <p role="status">Loading pages…</p>}
                {pages.error && (
                  <p role="alert">{automationError(pages.error)}</p>
                )}
                <div className="space-y-3">
                  {pages.data?.pages
                    .flatMap((group) => group.data)
                    .map((page) => (
                      <article key={page.id} className="rounded-xl border p-5">
                        <div className="mb-3 flex items-center justify-between">
                          <h3 className="font-medium">
                            Page {page.pageNumber}
                          </h3>
                          {links[
                            `${file.data.processingId}:${page.pageNumber}`
                          ] ? (
                            <a
                              href={
                                links[
                                  `${file.data.processingId}:${page.pageNumber}`
                                ]
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary inline-flex items-center gap-1 text-sm underline"
                            >
                              Open source <ExternalLink className="size-3" />
                            </a>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={source.isPending}
                              onClick={() => {
                                if (file.data.processingId)
                                  source.mutate({
                                    page: page.pageNumber,
                                    processingId: file.data.processingId,
                                  });
                              }}
                            >
                              View source
                            </Button>
                          )}
                        </div>
                        <p className="text-sm leading-6 whitespace-pre-wrap">
                          {page.summary ?? "No summary available."}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {page.tags.map((tag) => (
                            <span
                              key={tag.id}
                              className="bg-muted rounded-full px-2 py-1 text-xs"
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      </article>
                    ))}
                </div>
                {source.error && (
                  <p role="alert">{automationError(source.error)}</p>
                )}
                {pages.hasNextPage && (
                  <Button
                    className="mt-4"
                    variant="outline"
                    disabled={pages.isFetchingNextPage}
                    onClick={() => void pages.fetchNextPage()}
                  >
                    {pages.isFetchingNextPage ? "Loading…" : "More pages"}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AppPageShell>
  );
}
